import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { LOT_STATUS } from 'constants/common';
import { LotStatus } from 'constants/enums';
import { displayErrorMessage, makeAddress } from 'helpers/utils';
import CategoriesService from 'services/CategoriesService';
import { nominatimAPI } from 'services/leafletAPI';
import LotService from 'services/LotService';
import LotTypeService from 'services/LotTypeService';
import ProductTypeService from 'services/ProductTypeService';
import TagsService from 'services/TagsService';
import { preparePreviewData, prepareSingleLotData } from './helpers';
import { singleLotForEdit } from './constants';
import { RootStore } from 'stores/RootStore';
import { NavigateFunction } from 'react-router-dom';
import { Category, FormPreviewData, SingleLot, SingleLotForEdit } from 'stores/types/singleLot';
import { Tag } from 'stores/types/tag';
import { Image } from 'stores/types/image';

export class SingleLotStore {
  lotService: InstanceType<typeof LotService>;
  singleLot: Partial<SingleLot> = {};
  productTypes: any[] = [];
  lotTypes = [];
  tags: Tag[] = [];
  singleLotForEdit: Partial<SingleLotForEdit> = singleLotForEdit();
  singleLotNotFound: string | null = null;
  lotCategories: Partial<Category>[] = [{ id: 0, name: 'All', description: 'All categories' }];
  status: string;
  error: any;
  isLoading = false;

  constructor(private rootstore: RootStore) {
    this.lotService = new LotService();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  wipeSingleLot = () => {
    runInAction(() => {
      this.singleLot = {};
    });
  };

  getAddress = async (lon: string, lat: string) => {
    const response = await nominatimAPI.searchByCoordinates(lat, lon);
    if (!response || response.status !== 200 || !response.data.display_name) {
      return;
    }
    runInAction(() => {
      const { address } = response.data;
      const addressValue = makeAddress(address);

      this.singleLot = {
        ...this.singleLot,
        lotLocation: {
          ...this.singleLot.lotLocation,
          address: addressValue.trim(),
        },
      };
    });
  };

  getProductTypes = async () => {
    try {
      runInAction(() => (this.isLoading = true));
      const response = await ProductTypeService.getProductTypes();
      if (response.data && response.status === 200) {
        runInAction(() => {
          this.productTypes = response.data;
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  getLotTypes = async () => {
    try {
      runInAction(() => (this.isLoading = true));
      const response = await LotTypeService.getLotTypes();
      if (response.data && response.status === 200) {
        runInAction(() => {
          this.lotTypes = response.data;
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  getTags = async () => {
    try {
      runInAction(() => (this.isLoading = true));
      const response = await TagsService.getTags();

      if (response.data && response.status === 200) {
        runInAction(() => {
          this.tags = response.data;
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  getSingleLotAsync = async (lotId: string) => {
    const { currency } = this.rootstore.userStore;
    try {
      runInAction(() => (this.isLoading = true));
      const response = await this.lotService.getSingleLot(lotId, currency);
      runInAction(() => {
        if (response === undefined) {
          this.singleLotNotFound = null;
        } else {
          this.singleLotNotFound = '';
        }

        const address =
          this.singleLot.lotLocation && this.singleLot.lotLocation.address ? this.singleLot.lotLocation.address : '';
        this.singleLot = {
          ...response.data,
          lotLocation: {
            ...(response.data.lotLocation || {}),
            address,
          },
          images: response.data.images.sort((x: Image, y: Image) =>
            x.index === response.data.mainImageIndex ? -1 : y.index === response.data.mainImageIndex ? 1 : 0
          ),
        };

        if (
          !this.singleLot?.lotLocation?.address &&
          response.data.lotLocation &&
          response.data.lotLocation.longitude &&
          response.data.lotLocation.latitude
        ) {
          this.getAddress(response.data.lotLocation.longitude, response.data.lotLocation.latitude);
        }

        this.singleLotForEdit = prepareSingleLotData(response.data, this.singleLot);
        this.rootstore.lotStore.description = response.data.description;
      });
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
        if (error.response.status === 404) {
          this.singleLotNotFound = null;
        }
      });
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  activate = async (lotId: string) => {
    try {
      runInAction(() => (this.isLoading = true));
      const response = await this.lotService.activate(lotId);
      if (response && response.status === 200) {
        runInAction(() => {
          this.singleLot = { ...this.singleLot, active: true, lotStatus: LotStatus.Published };
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.error = error;
      });
      displayErrorMessage(error);
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  deactivate = async (lotId: string, navigate: NavigateFunction) => {
    try {
      runInAction(() => (this.isLoading = true));
      const response = await this.lotService.deactivate(lotId);
      if (response && response.status === 200) {
        runInAction(() => {
          this.singleLot = { ...this.singleLot, active: false, lotStatus: LotStatus.Deactivated };
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.error = error;
      });
      displayErrorMessage(error);
      if (error.response.status && error.response.status === 404) {
        navigate('/');
      }
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  resetSingleLots = () => {
    runInAction(() => {
      this.singleLot = {};
      this.singleLotForEdit = {
        id: '',
        title: '',
        categoryId: '',
        lotState: '',
        fixedPrice: '',
        currency: '',
        lotImagesToAdd: [],
        imagesIdsToDelete: [],
      };
    });
  };

  async loadProductTypes() {
    try {
      const response = await ProductTypeService.getProductTypes();
      if (response && response.status === 200) {
        runInAction(() => {
          this.productTypes = [...response.data];
        });
      }
    } catch (error) {
      toast.error('An error occurred while trying to get product types, please try later.');
    }
  }

  getCategories = async () => {
    if (this.lotCategories.length > 1) {
      return;
    }
    try {
      const response = await CategoriesService.getCategories();
      if (response.data && response.status === 200) {
        runInAction(() => {
          const categories = (response.data as Category[]).map(cat => ({
            ...cat,
            link: cat.name.toLowerCase().split(' ').join('-'),
          }));

          this.lotCategories = [{ ...this.lotCategories[0], link: '' }, ...categories];
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
      });
      if (error.response && error.response.data) {
        toast.error(error.response.data);
      }
    }
  };

  setDataForPreview = (formData: FormPreviewData) => {
    runInAction(() => {
      this.singleLotNotFound = '';

      const previewData = {
        ...formData,
        contact: { ...this.rootstore.userStore.userProfile, phone: this.rootstore.userStore.userProfile.phoneNumber },
        location: this.rootstore.userStore.locations.find(({ id }) => id === formData.locationId) || null,
        lotStatus: LOT_STATUS.PUBLISHED,
      };

      this.singleLot = preparePreviewData(previewData);
    });
  };

  get active() {
    return this.singleLot.lotStatus === LOT_STATUS.PUBLISHED;
  }

  get inactiveLot() {
    return this.singleLot.lotStatus !== LOT_STATUS.PUBLISHED;
  }

  get isDraft() {
    return this.singleLot.lotStatus === LOT_STATUS.DRAFT;
  }
}
